import { useState } from 'react';
import {
  useDashboardMetrics,
  useClientVisitsReport,
  useStaffTimeReport,
  useCANSComplianceReport,
  useDemographicsReport,
  useDonorReport,
} from '../hooks/useReports';
import {
  getClientVisitsExport,
  getStaffTimeExport,
  getCANSComplianceExport,
  getDemographicsExport,
  getDonorExport,
  ReportParams,
} from '../services/reportsService';
import { exportToCsv, csvFilenameDate } from '../utils/csvExport';
import { exportToXlsx } from '../utils/xlsxExport';
import '../styles/FoodDistribution.css';

type TabId = 'client-visits' | 'staff-time' | 'cans-compliance' | 'demographics' | 'donor-financial';

function TabButton({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button className={`fd-tab${active ? ' active' : ''}`} onClick={onClick}>
      {label}
    </button>
  );
}

// ─── Quick date preset helpers ─────────────────────────────────────────────────

function getThisMonthRange(): { startDate: string; endDate: string } {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  return {
    startDate: start.toISOString().slice(0, 10),
    endDate: now.toISOString().slice(0, 10),
  };
}

function getLastMonthRange(): { startDate: string; endDate: string } {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const end = new Date(now.getFullYear(), now.getMonth(), 0);
  return {
    startDate: start.toISOString().slice(0, 10),
    endDate: end.toISOString().slice(0, 10),
  };
}

// ─── Stat Card ─────────────────────────────────────────────────────────────────

function StatCard({ label, value, sub }: { label: string; value: string | number; sub?: string }) {
  return (
    <div className="fd-stat-card">
      <span className="fd-stat-label">{label}</span>
      <span className="fd-stat-value">{value}</span>
      {sub && <span className="fd-stat-sub">{sub}</span>}
    </div>
  );
}

// ─── Client Visits Tab ─────────────────────────────────────────────────────────

function ClientVisitsTab({ params }: { params: ReportParams }) {
  const { data, isLoading, error } = useClientVisitsReport(params);

  if (isLoading) {return <p style={{ color: '#888' }}>Loading client visits data...</p>;}
  if (error) {return <p style={{ color: '#c62828' }}>Failed to load client visits data.</p>;}
  if (!data) {return <p style={{ color: '#aaa' }}>No data for the selected period.</p>;}

  const topProgram = data.summary.programBreakdown?.length
    ? data.summary.programBreakdown.sort((a, b) => b.visitCount - a.visitCount)[0].programName
    : '—';

  return (
    <div>
      <div className="fd-stat-grid">
        <StatCard label="Total Visits" value={data.summary.totalVisits ?? '—'} sub="service interactions" />
        <StatCard label="Unique Clients" value={data.summary.uniqueClients ?? '—'} sub="unduplicated" />
        <StatCard label="Contact Hours" value={data.summary.totalContactHours ?? '—'} sub="total logged" />
        <StatCard label="Top Program" value={topProgram} sub="most visits" />
      </div>

      {data.records.length > 0 ? (
        <div className="fd-table-wrap">
          <table className="fd-table">
            <thead>
              <tr>
                <th>Visit Date</th>
                <th>Client ID</th>
                <th>Client Name</th>
                <th>Client Type</th>
                <th>Program / Service</th>
                <th>Duration (min)</th>
                <th>Case Note</th>
              </tr>
            </thead>
            <tbody>
              {data.records.map((r, i) => (
                <tr key={i}>
                  <td>{r.visitDate}</td>
                  <td>{r.clientId}</td>
                  <td>{r.clientName}</td>
                  <td>{r.clientType}</td>
                  <td>{r.programName}</td>
                  <td>{r.durationMinutes ?? '—'}</td>
                  <td style={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {r.caseNote || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p style={{ color: '#aaa', marginTop: '1rem' }}>No visit records for the selected period.</p>
      )}

      {data.pagination && (
        <p style={{ fontSize: '0.8125rem', color: '#888', marginTop: '0.5rem' }}>
          Showing {data.records.length} of {data.pagination.totalItems} records
          (page {data.pagination.page} of {data.pagination.totalPages})
        </p>
      )}
    </div>
  );
}

// ─── Staff Time Tab ────────────────────────────────────────────────────────────

function StaffTimeTab({ params }: { params: ReportParams }) {
  const { data, isLoading, error } = useStaffTimeReport(params);

  if (isLoading) {return <p style={{ color: '#888' }}>Loading staff time data...</p>;}
  if (error) {return <p style={{ color: '#c62828' }}>Failed to load staff time data.</p>;}
  if (!data) {return <p style={{ color: '#aaa' }}>No data for the selected period.</p>;}

  return (
    <div>
      <div className="fd-stat-grid">
        <StatCard label="Active Staff" value={data.summary.totalStaff ?? '—'} sub="with logged time" />
        <StatCard label="Total Hours" value={data.summary.totalHours ?? '—'} sub="all staff combined" />
        <StatCard label="Total Contacts" value={data.summary.totalVisits ?? '—'} sub="contact log entries" />
      </div>

      {data.records.length > 0 ? (
        <div className="fd-table-wrap">
          <table className="fd-table">
            <thead>
              <tr>
                <th>Staff Name</th>
                <th>Email</th>
                <th>Visit Count</th>
                <th>Total Minutes</th>
                <th>Total Hours</th>
              </tr>
            </thead>
            <tbody>
              {data.records.map((r, i) => (
                <tr key={i}>
                  <td>{r.staffName}</td>
                  <td>{r.staffEmail}</td>
                  <td>{r.visitCount}</td>
                  <td>{r.totalMinutes}</td>
                  <td>{r.totalHours.toFixed(1)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p style={{ color: '#aaa', marginTop: '1rem' }}>No staff time records for the selected period.</p>
      )}
    </div>
  );
}

// ─── CANS Compliance Tab ───────────────────────────────────────────────────────

function CANSComplianceTab() {
  const { data, isLoading, error } = useCANSComplianceReport();

  if (isLoading) {return <p style={{ color: '#888' }}>Loading CANS compliance data...</p>;}
  if (error) {return <p style={{ color: '#c62828' }}>Failed to load CANS compliance data.</p>;}
  if (!data) {return <p style={{ color: '#aaa' }}>No CANS compliance data available.</p>;}

  const compliancePct = data.summary.complianceRate != null
    ? `${data.summary.complianceRate.toFixed(1)}%`
    : '—';

  return (
    <div>
      <div className="fd-stat-grid">
        <StatCard label="Total Assessments" value={data.summary.totalAssessments ?? '—'} />
        <StatCard label="Compliant" value={data.summary.compliant ?? '—'} sub="up to date" />
        <StatCard label="Compliance Rate" value={compliancePct} />
        <StatCard label="Overdue" value={data.summary.overdue ?? '—'} sub="past due" />
        <StatCard label="Red Flags" value={data.summary.redFlags ?? '—'} sub="scores >= 2" />
      </div>

      {data.overdueList.length > 0 && (
        <div style={{ marginBottom: '1.5rem' }}>
          <h4 style={{ marginBottom: '0.75rem', color: '#5B2C86' }}>Overdue Assessments</h4>
          <div className="fd-table-wrap">
            <table className="fd-table">
              <thead>
                <tr>
                  <th>Client ID</th>
                  <th>Client Name</th>
                  <th>Assessment Type</th>
                  <th>Assessment Date</th>
                  <th>Next Due Date</th>
                  <th>Days Overdue</th>
                </tr>
              </thead>
              <tbody>
                {data.overdueList.map((r, i) => (
                  <tr key={i}>
                    <td>{r.clientId}</td>
                    <td>{r.clientName}</td>
                    <td>{r.assessmentType}</td>
                    <td>{r.assessmentDate}</td>
                    <td>{r.nextDueDate}</td>
                    <td style={{ color: '#c62828', fontWeight: 600 }}>{r.daysOverdue}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {data.redFlagList.length > 0 && (
        <div>
          <h4 style={{ marginBottom: '0.75rem', color: '#5B2C86' }}>Red Flag Assessments</h4>
          <div className="fd-table-wrap">
            <table className="fd-table">
              <thead>
                <tr>
                  <th>Client ID</th>
                  <th>Client Name</th>
                  <th>Assessment Type</th>
                  <th>Assessment Date</th>
                  <th>Next Due Date</th>
                </tr>
              </thead>
              <tbody>
                {data.redFlagList.map((r, i) => (
                  <tr key={i}>
                    <td>{r.clientId}</td>
                    <td>{r.clientName}</td>
                    <td>{r.assessmentType}</td>
                    <td>{r.assessmentDate}</td>
                    <td>{r.nextDueDate}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {data.overdueList.length === 0 && data.redFlagList.length === 0 && (
        <p style={{ color: '#2e7d32', marginTop: '1rem' }}>No overdue assessments or red flags — all clients current.</p>
      )}
    </div>
  );
}

// ─── Demographics Tab ──────────────────────────────────────────────────────────

function DemographicsTab() {
  const { data, isLoading, error } = useDemographicsReport();

  if (isLoading) {return <p style={{ color: '#888' }}>Loading demographics data...</p>;}
  if (error) {return <p style={{ color: '#c62828' }}>Failed to load demographics data.</p>;}
  if (!data) {return <p style={{ color: '#aaa' }}>No demographics data available.</p>;}

  return (
    <div>
      <div className="fd-stat-grid">
        <StatCard label="Total Clients" value={data.totalClients ?? '—'} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.25rem', marginTop: '0.5rem' }}>
        {data.byAge?.length > 0 && (
          <div>
            <h4 style={{ marginBottom: '0.75rem', color: '#5B2C86' }}>Age Distribution</h4>
            <div className="fd-table-wrap">
              <table className="fd-table">
                <thead><tr><th>Age Group</th><th>Count</th></tr></thead>
                <tbody>
                  {data.byAge.map((r, i) => (
                    <tr key={i}><td>{r.ageGroup}</td><td>{r.count}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {data.byGender?.length > 0 && (
          <div>
            <h4 style={{ marginBottom: '0.75rem', color: '#5B2C86' }}>Gender Breakdown</h4>
            <div className="fd-table-wrap">
              <table className="fd-table">
                <thead><tr><th>Gender</th><th>Count</th></tr></thead>
                <tbody>
                  {data.byGender.map((r, i) => (
                    <tr key={i}><td>{r.gender}</td><td>{r.count}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {data.byRace?.length > 0 && (
          <div>
            <h4 style={{ marginBottom: '0.75rem', color: '#5B2C86' }}>Race / Ethnicity</h4>
            <div className="fd-table-wrap">
              <table className="fd-table">
                <thead><tr><th>Race / Ethnicity</th><th>Count</th></tr></thead>
                <tbody>
                  {data.byRace.map((r, i) => (
                    <tr key={i}><td>{r.raceEthnicity}</td><td>{r.count}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {data.byZip?.length > 0 && (
          <div>
            <h4 style={{ marginBottom: '0.75rem', color: '#5B2C86' }}>Zip Code Breakdown</h4>
            <div className="fd-table-wrap">
              <table className="fd-table">
                <thead><tr><th>Zip Code</th><th>Count</th></tr></thead>
                <tbody>
                  {data.byZip.map((r, i) => (
                    <tr key={i}><td>{r.zipCode}</td><td>{r.count}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Donor & Financial Tab ─────────────────────────────────────────────────────

function DonorFinancialTab({ params }: { params: ReportParams }) {
  const { data, isLoading, error } = useDonorReport(params);

  if (isLoading) {return <p style={{ color: '#888' }}>Loading donor data...</p>;}
  if (error) {return <p style={{ color: '#c62828' }}>Failed to load donor data.</p>;}
  if (!data) {return <p style={{ color: '#aaa' }}>No donor data for the selected period.</p>;}

  const fmt = (n: number) =>
    n != null
      ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n)
      : '—';

  return (
    <div>
      <div className="fd-stat-grid">
        <StatCard label="Unique Donors" value={data.summary.uniqueDonors ?? '—'} />
        <StatCard label="Total Donations" value={data.summary.totalDonations ?? '—'} sub="transactions" />
        <StatCard label="Total Amount" value={fmt(data.summary.totalAmount)} />
        <StatCard label="Avg Gift" value={fmt(data.summary.averageAmount)} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.25rem', marginTop: '0.5rem' }}>
        {data.byCampaign?.length > 0 && (
          <div>
            <h4 style={{ marginBottom: '0.75rem', color: '#5B2C86' }}>By Campaign</h4>
            <div className="fd-table-wrap">
              <table className="fd-table">
                <thead><tr><th>Campaign</th><th>Donations</th><th>Total</th></tr></thead>
                <tbody>
                  {data.byCampaign.map((r, i) => (
                    <tr key={i}><td>{r.campaignName}</td><td>{r.donationCount}</td><td>{fmt(r.totalAmount)}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {data.byPaymentMethod?.length > 0 && (
          <div>
            <h4 style={{ marginBottom: '0.75rem', color: '#5B2C86' }}>By Payment Method</h4>
            <div className="fd-table-wrap">
              <table className="fd-table">
                <thead><tr><th>Method</th><th>Count</th><th>Total</th></tr></thead>
                <tbody>
                  {data.byPaymentMethod.map((r, i) => (
                    <tr key={i}><td>{r.paymentMethod}</td><td>{r.donationCount}</td><td>{fmt(r.totalAmount)}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {data.byDonorType?.length > 0 && (
          <div>
            <h4 style={{ marginBottom: '0.75rem', color: '#5B2C86' }}>By Donor Type</h4>
            <div className="fd-table-wrap">
              <table className="fd-table">
                <thead><tr><th>Donor Type</th><th>Count</th><th>Total</th></tr></thead>
                <tbody>
                  {data.byDonorType.map((r, i) => (
                    <tr key={i}><td>{r.donorType}</td><td>{r.donationCount}</td><td>{fmt(r.totalAmount)}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────

export function ReportsPage() {
  const [activeTab, setActiveTab] = useState<TabId>('client-visits');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [exporting, setExporting] = useState(false);

  const { data: dashboardData } = useDashboardMetrics();

  const reportParams: ReportParams = {
    startDate: startDate || undefined,
    endDate: endDate || undefined,
  };

  const handleExport = async (format: 'csv' | 'xlsx') => {
    setExporting(true);
    try {
      const filename = `tacolcy-${activeTab}-${csvFilenameDate()}`;
      let records: Record<string, unknown>[];
      let columns: { key: string; label: string; width?: number }[] | undefined;

      switch (activeTab) {
        case 'client-visits': {
          const res = await getClientVisitsExport(reportParams);
          records = res.records as unknown as Record<string, unknown>[];
          columns = [
            { key: 'visitDate', label: 'Visit Date' },
            { key: 'clientId', label: 'Client ID' },
            { key: 'clientName', label: 'Client Name' },
            { key: 'clientType', label: 'Client Type' },
            { key: 'programName', label: 'Program / Service' },
            { key: 'durationMinutes', label: 'Duration (min)' },
            { key: 'caseNote', label: 'Case Note' },
          ];
          break;
        }
        case 'staff-time': {
          const res = await getStaffTimeExport(reportParams);
          records = res.records as unknown as Record<string, unknown>[];
          columns = [
            { key: 'staffName', label: 'Staff Name' },
            { key: 'staffEmail', label: 'Email' },
            { key: 'visitCount', label: 'Visit Count' },
            { key: 'totalMinutes', label: 'Total Minutes' },
            { key: 'totalHours', label: 'Total Hours' },
          ];
          break;
        }
        case 'cans-compliance': {
          const res = await getCANSComplianceExport();
          records = res.records;
          break;
        }
        case 'demographics': {
          const res = await getDemographicsExport();
          records = res.records;
          break;
        }
        case 'donor-financial': {
          const res = await getDonorExport(reportParams);
          records = res.records;
          break;
        }
        default:
          records = [];
      }

      if (!records || records.length === 0) {
        alert('No data to export for the selected period.');
        return;
      }

      if (format === 'xlsx') {
        exportToXlsx(records, filename, columns);
      } else {
        exportToCsv(records, filename, columns);
      }
    } catch (err: unknown) {
      alert('Export failed: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div>
      <div className="page-header">
        <h2>Reports &amp; Analytics</h2>
        <p>Service delivery, compliance, demographics, and financial summaries</p>
      </div>

      {/* Dashboard summary bar */}
      {dashboardData && (
        <div className="fd-stat-grid" style={{ marginBottom: '1.5rem' }}>
          <StatCard label="Total Clients" value={dashboardData.totalClients} />
          <StatCard label="Active Families" value={dashboardData.activeFamilies} />
          <StatCard label="Service Hours" value={dashboardData.serviceHours} />
          <StatCard label="CANS Due" value={dashboardData.cansDue} />
          <StatCard label="Total Donors" value={dashboardData.totalDonors} />
          <StatCard label="Red Flags" value={dashboardData.redFlags} />
        </div>
      )}

      {/* Filter bar */}
      <div className="fd-filter-bar" style={{ marginBottom: '1.25rem' }}>
        <label>
          Start Date
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        </label>
        <label>
          End Date
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
        </label>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end' }}>
          <button
            type="button"
            onClick={() => {
              const r = getThisMonthRange();
              setStartDate(r.startDate);
              setEndDate(r.endDate);
            }}
            style={{ padding: '0.5rem 0.75rem', border: '1px solid #ddd', borderRadius: 5, background: 'white', cursor: 'pointer', fontSize: '0.85rem', fontFamily: 'Montserrat, sans-serif' }}
          >
            This Month
          </button>
          <button
            type="button"
            onClick={() => {
              const r = getLastMonthRange();
              setStartDate(r.startDate);
              setEndDate(r.endDate);
            }}
            style={{ padding: '0.5rem 0.75rem', border: '1px solid #ddd', borderRadius: 5, background: 'white', cursor: 'pointer', fontSize: '0.85rem', fontFamily: 'Montserrat, sans-serif' }}
          >
            Last Month
          </button>
          <button
            type="button"
            onClick={() => { setStartDate(''); setEndDate(''); }}
            style={{ padding: '0.5rem 0.75rem', border: '1px solid #ddd', borderRadius: 5, background: 'white', cursor: 'pointer', fontSize: '0.85rem', fontFamily: 'Montserrat, sans-serif' }}
          >
            All Time
          </button>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem', alignItems: 'flex-end' }}>
          <button
            className="fd-export-btn"
            onClick={() => handleExport('xlsx')}
            disabled={exporting}
            style={{ background: '#5B2C86', color: 'white' }}
          >
            {exporting ? 'Exporting...' : 'Export XLSX'}
          </button>
          <button
            className="fd-export-btn"
            onClick={() => handleExport('csv')}
            disabled={exporting}
            style={{ background: 'white', color: '#5B2C86', border: '1px solid #5B2C86' }}
          >
            {exporting ? 'Exporting...' : 'Export CSV'}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="fd-tabs">
        <TabButton label="Client Visits" active={activeTab === 'client-visits'} onClick={() => setActiveTab('client-visits')} />
        <TabButton label="Staff &amp; Time" active={activeTab === 'staff-time'} onClick={() => setActiveTab('staff-time')} />
        <TabButton label="CANS Compliance" active={activeTab === 'cans-compliance'} onClick={() => setActiveTab('cans-compliance')} />
        <TabButton label="Demographics" active={activeTab === 'demographics'} onClick={() => setActiveTab('demographics')} />
        <TabButton label="Donor &amp; Financial" active={activeTab === 'donor-financial'} onClick={() => setActiveTab('donor-financial')} />
      </div>

      <div>
        {activeTab === 'client-visits' && <ClientVisitsTab params={reportParams} />}
        {activeTab === 'staff-time' && <StaffTimeTab params={reportParams} />}
        {activeTab === 'cans-compliance' && <CANSComplianceTab />}
        {activeTab === 'demographics' && <DemographicsTab />}
        {activeTab === 'donor-financial' && <DonorFinancialTab params={reportParams} />}
      </div>
    </div>
  );
}
